import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Lock, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useScrollToDownload } from "@/lib/useScrollToDownload";
import { trackCta } from "@/lib/analytics";
import { useT } from "@/i18n/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";

// Primary destinations — calm bar (Founder decision, 2026-07-29):
// HOME · ACADEMY · LIBRARY · ABOUT US · MY STOICOS.
const primaryLinks = [
  { to: "/",           labelKey: "nav.home" },
  { to: "/academy",    labelKey: "academy.nav" },
  { to: "/library",    labelKey: "nav.library" },
  { to: "/ancient-library", labelKey: "nav.ancientLibrary" },
  { to: "/about",      labelKey: "nav.about" },
  { to: "/my-stoicos", labelKey: "nav.mystoicos" },
];

// Secondary pages — reachable through the hamburger menu on every size.
const secondaryLinks = [
  { to: "/support", labelKey: "nav.support" },
];

// Full ecosystem navigation for the sidebar/hamburger menu (Founder order):
// HOME · ACADEMY · THE STOIC JOURNAL · AGORA · LIVING LIBRARY · ABOUT US · MY STOICOS.
const menuLinks = [
  { to: "/",              labelKey: "nav.home",         testKey: "home" },
  { to: "/academy",       labelKey: "academy.nav",      testKey: "academy" },
  { to: "/stoic-journal", labelKey: "nav.stoicJournal", testKey: "journal" },
  { to: "/agora",         labelKey: "nav.agoraMain",    testKey: "agora" },
  { to: "/library",       labelKey: "nav.library",      testKey: "library" },
  { to: "/ancient-library", labelKey: "nav.ancientLibrary", testKey: "ancientlibrary" },
  { to: "/about",         labelKey: "nav.aboutUs",      testKey: "about" },
  { to: "/my-stoicos",    labelKey: "nav.mystoicos",    testKey: "mystoicos" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const goDownload = useScrollToDownload();
  const { t } = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/75 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1560px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-home-link" aria-label="StoicOs home" className="shrink-0">
          <Logo />
        </Link>

        {/* Desktop primary nav — HOME is always visible. Secondary pages
            live behind the hamburger, which stays present on desktop too. */}
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6">
          {primaryLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.labelKey.split('.')[1]}`}
              className={({ isActive }) =>
                `text-[10px] xl:text-[11px] tracking-[0.16em] xl:tracking-[0.18em] uppercase font-medium transition-colors whitespace-nowrap ${
                  isActive ? "text-gold" : "text-bone/70 hover:text-bone"
                }`
              }
              end={l.to === "/"}
            >
              {t(l.labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 lg:gap-4">
          <div className="hidden lg:flex items-center gap-4">
          <LanguageSelector prefix="lang" align="right" />
          <a
            href="/#download"
            onClick={(e) => { trackCta("download_nav", { placement: "header_desktop" }); goDownload(e); }}
            data-testid="nav-download-cta"
            className="text-[12px] tracking-[0.2em] uppercase text-ink bg-gold hover:bg-gold-hover px-5 py-2.5 transition-colors cursor-pointer"
          >
            {t("nav.download")}
          </a>
          </div>

          {/* Mobile-only language selector (desktop shows the selector above) */}
          <div className="lg:hidden" data-testid="mobile-lang-pill">
            <LanguageSelector prefix="mobile-lang" align="right" />
          </div>
          <button
            className="text-gold p-2.5 bg-black/70 border border-gold/70 hover:border-gold rounded-full backdrop-blur-sm transition-colors"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={18} strokeWidth={1.75} /> : <Menu size={18} strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="bg-black/95 backdrop-blur-xl border-t border-white/5 max-h-[calc(100dvh-5rem)] overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="px-6 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] flex flex-col gap-5">
            {/* Full ecosystem navigation — same order on phone, tablet and desktop. */}
            {menuLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.testKey}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-sm tracking-[0.18em] uppercase ${
                    isActive ? "text-gold" : "text-bone/80 hover:text-bone"
                  }`
                }
                end={l.to === "/"}
              >
                {t(l.labelKey)}
              </NavLink>
            ))}
            <div className="lg:hidden" data-testid="mobile-lang-toggle">
              <LanguageSelector prefix="mobile-menu-lang" align="left" />
            </div>
            <a
              href="/#download"
              data-testid="mobile-nav-download-cta"
              className="lg:hidden mt-2 text-[12px] tracking-[0.2em] uppercase text-ink bg-gold px-5 py-3 inline-block text-center cursor-pointer"
              onClick={(e) => { trackCta("download_nav", { placement: "header_mobile" }); setOpen(false); goDownload(e); }}
            >
              {t("nav.download")}
            </a>
            <div className="my-1 border-t border-white/10" aria-hidden="true" />
            {secondaryLinks.map((l) => l.href ? (
              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
                data-testid={`mobile-nav-link-${l.labelKey.split('.')[1]}`}
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.18em] uppercase text-bone/80">
                {t(l.labelKey)}
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.labelKey.split('.')[1]}`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.18em] uppercase ${
                    isActive ? "text-gold" : "text-bone/80"
                  }`
                }
                end={l.to === "/"}
              >
                {t(l.labelKey)}
              </NavLink>
            ))}
            <Link
              to="/admin"
              data-testid="mobile-nav-founder-portal"
              onClick={() => setOpen(false)}
              className="mt-6 pt-4 border-t border-white/5 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-fog/50 hover:text-gold/70 transition-colors"
            >
              <Lock size={12} strokeWidth={1.5} />
              Founder Portal
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
